<template>
  <div
    v-if="isQuickNote"
    class="modal z-[101]"
    :class="{ 'z-[99]': otherQuickResourcesAreOn || uploadPopupIsOn }"
    @mousedown="(noteId) => emit('close', noteId)"
  >
    <div
      class="w-[600px] h-[600px] border border-solid border-important-border rounded-lg overflow-hidden"
      @mousedown.stop
    >
      <NoteEditor
        :note-id="noteId"
        :is-quick-note="true"
        @createNote="emitCreateNote"
        @close="(noteId) => emit('close', noteId)"
      />
      <Transition>
        <NoteEnhancingOverlay
          v-if="aiNoteGenerating || aiNoteGeneratingFinishedTimeout"
          :finished="aiNoteGeneratingFinishedTimeout"
          :transcript-available="noteHasTranscript"
        />
      </Transition>
      <NoteEditorBar
        class="sticky bottom-0"
        :note-id="noteId"
        :is-quick-note="true"
      />
    </div>
  </div>
  <div
    v-else-if="isElectron"
    class="fullscreen-note-wrap"
  >
    <ElectronNoteHeader />
    <NoteEditor
      :note-id="noteId"
      @createNote="emitCreateNote"
    />
    <Transition>
      <NoteEnhancingOverlay
        v-if="aiNoteGenerating || aiNoteGeneratingFinishedTimeout"
        :finished="aiNoteGeneratingFinishedTimeout"
        :transcript-available="noteHasTranscript"
      />
    </Transition>
    <NoteEditorBar
      class="mx-auto sticky bottom-0 w-[700px]"
      :note-id="noteId"
      :is-electron-quick-note="true"
    />
  </div>

  <div
    v-else
    class="note-wrap"
  >
    <NoteEditorBar :note-id="noteId" />
    <NoteEditor
      :note-id="noteId"
      @createNote="emitCreateNote"
    />
    <Transition>
      <NoteEnhancingOverlay
        v-if="aiNoteGenerating || aiNoteGeneratingFinishedTimeout"
        :finished="aiNoteGeneratingFinishedTimeout"
        :transcript-available="noteHasTranscript"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import NoteEditorBar from '@/components/notes/NoteEditorBar';
import NoteEditor from '@/components/notes/NoteEditor';
import ElectronNoteHeader from '@/components/notes/ElectronNoteHeader';
import { ref, computed, watch, toRef } from 'vue';
import NoteEnhancingOverlay from '@/components/notes/NoteEnhancingOverlay';
import { useStore } from 'vuex';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

const FINISH_TIMEOUT_RATE = 3000;

const store = useStore();
const { isGuestReader } = useCurrentRole();

const emit = defineEmits(['createNote', 'deleteNote', 'close']);

const props = defineProps({
  noteId: {
    type: String,
    required: true,
  },
  isElectron: {
    type: Boolean,
    required: false,
    default: false,
  },
  isQuickNote: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const aiNoteGeneratingFinishedTimeout = ref();

const note = computed(() => store.getters.getNotes[props.noteId] || {});

const noteHasTranscript = computed(() => !!note.value.transcript);

const aiNoteGenerating = computed(() => store.getters.getAiNoteGenerating);

const otherQuickResourcesAreOn = computed(() => {
  return (store.getters.getQuickCardIsOn || store.getters.getQuickPage) && !isGuestReader.value;
});

const uploadPopupIsOn = computed(() => Object.keys(store.getters.getPageUploadPopup).length > 0);

const clearAiNoteGeneratingFinishedTimeout = () => {
  clearTimeout(aiNoteGeneratingFinishedTimeout.value);
  aiNoteGeneratingFinishedTimeout.value = null;
};

const emitCreateNote = (noteId: string) => {
  emit('createNote', noteId);
};

window.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  emit('close');
});

watch(aiNoteGenerating, (newValue) => {
  /**
   * When AI note generation finishes, we set a timeout
   * to show the finished message for a certain amount of time
   */
  if (!newValue) {
    aiNoteGeneratingFinishedTimeout.value = setTimeout(
      clearAiNoteGeneratingFinishedTimeout,
      FINISH_TIMEOUT_RATE
    );
  }
});

watch(toRef(props, 'noteId'), () => {
  clearAiNoteGeneratingFinishedTimeout();
});
</script>

<style lang="scss">
.note-wrap {
  @apply flex flex-col relative h-screen;

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}

.fullscreen-note-wrap {
  @apply flex flex-col w-full h-screen bg-background-strongest z-[14] relative;
}
</style>
